// types/livetv.ts — mirrors the backend services/livetv/livetv.types.ts.

export type LiveTvCategory =
  | 'sports'
  | 'news'
  | 'movies'
  | 'entertainment'
  | 'kids'
  | 'music'
  | 'documentary'
  | 'general';

/** Which upstream catalog a channel came from. `daddylive` is the dlhd.pk 24/7
 *  directory; `iptv` is the iptv-org catalog (a direct, publicly-listed HLS
 *  URL). Optional for backward-compat with responses that predate the field. */
export type LiveTvProvider = 'daddylive' | 'iptv';

/** A selectable variant of a channel — same logical channel from a different
 *  catalog/country/language. `id` is the variant's own resolvable channel id.
 *  Mirrors the backend LiveTvSource. */
export interface LiveTvSource {
  id: string;
  provider: LiveTvProvider;
  language: string | null;
  country: string | null;
  label: string;
}

export interface LiveTvChannel {
  id: string;
  name: string;
  category: LiveTvCategory;
  country: string | null;
  /** Source catalog. Absent on legacy responses → treated as 'daddylive'. */
  provider?: LiveTvProvider;
  /** Channel logo URL (iptv-org channels), or null. */
  logo?: string | null;
  /** Display language name (e.g. "English"), or null. */
  language?: string | null;
  /** Selectable variants for the language toggle. Present on the detail
   *  response; the first entry is this (canonical) channel. */
  sources?: LiveTvSource[];
  /** Sub-project #2: raw CDN HLS URL for a HEADER-FREE iptv channel, present
   *  only on the single-channel detail response and only when the backend
   *  STREAMING_IPTVORG_DIRECT_ENABLED flag is on. When set, WatchLiveTV loads it
   *  directly from the viewer's residential IP (geo-bypass + zero server
   *  bandwidth), falling back to the server proxy on any failure. */
  directUrl?: string;
}

/** Display order + labels for the category filter chips. */
export const LIVE_TV_CATEGORIES: Array<{ id: LiveTvCategory; label: string }> = [
  { id: 'sports', label: 'Sports' },
  { id: 'news', label: 'News' },
  { id: 'movies', label: 'Movies' },
  { id: 'entertainment', label: 'Entertainment' },
  { id: 'documentary', label: 'Documentary' },
  { id: 'kids', label: 'Kids' },
  { id: 'music', label: 'Music' },
  { id: 'general', label: 'General' },
];

export function categoryLabel(id: LiveTvCategory): string {
  return LIVE_TV_CATEGORIES.find((c) => c.id === id)?.label ?? 'General';
}
